import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/20/solid';

import { useAuth } from '../../hooks/use-auth';
import * as categoriesService from '../../services/api/categories';
import * as languagesService from '../../services/api/languages';
import { Category } from '../../entities/category';
import { CategoryForm } from '../../entities/form/category';
import { Language } from '../../entities/language';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonButtonDefault from '../../components/common/buttons/default';
import CommonConfirmationModal from '../../components/modals/confirmation';
import Heading1 from '../../components/common/headings/heading1';
import LabelWithError from '../../components/common/form/label-with-error';
import StyledInput from '../../components/common/form/styled-input';
import { strings } from '../../localization/strings';

interface ComponentProps {
  isEdit?: boolean;
}

const cardClassName = 'rounded-lg bg-white ring-1 ring-gray-900/5';
const selectClassName =
  'block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6';

const categoryTypes = [
  { id: 0, label: strings.categories.types.news },
  { id: 1, label: strings.categories.types.eltaGuide },
  { id: 2, label: strings.categories.types.expectedEvents },
];

const defaultValues: CategoryForm = {
  lang: 0,
  type: 0,
  name: '',
  parent_id: null,
  abbreviation: '',
  abbreviation_foreign: '',
  intro: '',
  is_active: 1,
  in_homepage: 0,
  for_news: 1,
  for_photos: 1,
  for_pressreleases: 1,
  sort_order: null,
  sort_order_bulletin: null,
  extra: null,
  mapped_to: null,
  assign: 0,
};

const getCategoryDepth = (category: Category, categories: Category[]): number => {
  if (!category.parent_id) {
    return 0;
  }

  const parent = categories.find((item) => item.id === category.parent_id);

  return parent ? getCategoryDepth(parent, categories) + 1 : 0;
};

function CategoriesEditPageComponent({ isEdit }: ComponentProps) {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [languages, setLanguages] = useState<Language[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const {
    register,
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CategoryForm>({ defaultValues });
  const selectedLanguage = useWatch({ control, name: 'lang' });
  const selectedType = useWatch({ control, name: 'type' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const activeLanguages = await languagesService.list(token);

        setLanguages(activeLanguages);

        if (isEdit && id) {
          const category = await categoriesService.one(token, id);

          reset({
            ...defaultValues,
            lang: category.lang,
            type: category.type,
            name: category.name,
            parent_id: category.parent_id || null,
            abbreviation: category.abbreviation || '',
            abbreviation_foreign: category.abbreviation_foreign || '',
            intro: category.intro || '',
            is_active: Number(category.is_active),
            in_homepage: Number(category.in_homepage || 0),
            for_news: Number(category.for_news || 0),
            for_photos: Number(category.for_photos || 0),
            for_pressreleases: Number(category.for_pressreleases || 0),
            sort_order: category.sort_order,
            sort_order_bulletin: category.sort_order_bulletin,
          });
        } else {
          reset({ ...defaultValues, lang: activeLanguages[0]?.legacy_id ?? 0 });
        }

        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        setError((e as Error).message);
      }
    };

    fetchData();
  }, [id, isEdit, reset, token]);

  useEffect(() => {
    const language = languages.find((item) => item.legacy_id === selectedLanguage);

    if (!language || typeof selectedType !== 'number') {
      return;
    }

    const fetchCategories = async () => {
      const response = await categoriesService.list(token, {
        language_id: language.id,
        type: selectedType,
        limit: -1,
        is_active: 1,
        sort: 'sort_order,id',
      });

      setCategories(response.items.filter((category) => category.id !== Number(id)));
    };

    fetchCategories().catch((e) => setError((e as Error).message));
  }, [id, languages, selectedLanguage, selectedType, token]);

  const parentOptions = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        label: `${'\u00a0'.repeat(getCategoryDepth(category, categories) * 4)}${category.name}`,
      })),
    [categories],
  );

  const onSubmit = async (data: CategoryForm) => {
    try {
      setIsLoading(true);
      const payload = {
        ...data,
        is_active: data.is_active ? 1 : 0,
        in_homepage: data.in_homepage ? 1 : 0,
        for_news: data.for_news ? 1 : 0,
        for_photos: data.for_photos ? 1 : 0,
        for_pressreleases: data.for_pressreleases ? 1 : 0,
        parent_id: data.parent_id || null,
        assign: data.assign ? 1 : 0,
      };

      if (isEdit && id) {
        await categoriesService.update(token, Number(id), payload);
      } else {
        await categoriesService.create(token, payload);
      }

      const language = languages.find((item) => item.legacy_id === data.lang);

      navigate(`/categories?lang=${language?.id || ''}&type=${data.type}`);
    } catch (e) {
      setIsLoading(false);
      setError((e as Error).message);
    }
  };

  const onDelete = async () => {
    if (!id) {
      return;
    }

    try {
      setIsLoading(true);
      await categoriesService.remove(token, Number(id));
      navigate('/categories');
    } catch (e) {
      setIsLoading(false);
      setError((e as Error).message);
    }
  };

  return (
    <div>
      {isLoading && <CommonAnimatedLoader />}
      {!isLoading && (
        <>
          <nav className="flex" aria-label={strings.categories.title}>
            <ol className="flex items-center space-x-4">
              <li>
                <a href="/" className="text-gray-400 hover:text-gray-500">
                  <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span className="sr-only">{strings.common.home}</span>
                </a>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <Link to="/categories" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                    {strings.categories.title}
                  </Link>
                </div>
              </li>
            </ol>
          </nav>
          <header className="mb-3 mt-3">
            <Heading1>{isEdit ? strings.categories.editTitle : strings.categories.createTitle}</Heading1>
          </header>
          <main>
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
              {error && <AlertPrimary type="danger" text={error} />}
              <form onSubmit={handleSubmit(onSubmit)}>
                <section className={cardClassName}>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-4 sm:grid-cols-12">
                    <div className="sm:col-span-12">
                      <LabelWithError title={strings.categories.form.name} error={errors.name?.message} hasStar>
                        <StyledInput
                          type="text"
                          {...register('name', { required: strings.categories.form.nameRequired })}
                          hasError={!!errors.name}
                        />
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-4">
                      <LabelWithError title={strings.categories.form.language} error={undefined}>
                        <select {...register('lang', { valueAsNumber: true })} className={selectClassName}>
                          {languages.map((language) => (
                            <option key={language.id} value={language.legacy_id}>
                              {language.name}
                            </option>
                          ))}
                        </select>
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-4">
                      <LabelWithError title={strings.categories.form.type} error={undefined}>
                        <select {...register('type', { valueAsNumber: true })} className={selectClassName}>
                          {categoryTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-4">
                      <LabelWithError title={strings.categories.form.parent} error={undefined}>
                        <select
                          {...register('parent_id', { valueAsNumber: true })}
                          className={selectClassName}
                          defaultValue=""
                        >
                          <option value="">{strings.categories.form.noParent}</option>
                          {parentOptions.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-6">
                      <LabelWithError title={strings.categories.form.abbreviation} error={undefined}>
                        <StyledInput type="text" {...register('abbreviation')} />
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-6">
                      <LabelWithError title={strings.categories.form.abbreviationForeign} error={undefined}>
                        <StyledInput type="text" {...register('abbreviation_foreign')} />
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-12">
                      <LabelWithError title={strings.categories.form.description} error={undefined}>
                        <textarea {...register('intro')} rows={4} className={selectClassName} />
                      </LabelWithError>
                    </div>
                    <div className="flex flex-wrap gap-6 sm:col-span-12">
                      {[
                        ['is_active', strings.categories.form.active],
                        ['in_homepage', strings.categories.form.homepage],
                        ['for_news', strings.categories.visibility.news],
                        ['for_photos', strings.categories.visibility.photos],
                        ['for_pressreleases', strings.categories.visibility.pressreleases],
                      ].map(([field, label]) => (
                        <label key={field} className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <input type="checkbox" value="1" {...register(field as keyof CategoryForm)} />
                          {label}
                        </label>
                      ))}
                    </div>
                    <div className="sm:col-span-6">
                      <LabelWithError title={strings.categories.form.sortOrder} error={undefined}>
                        <StyledInput type="number" {...register('sort_order', { valueAsNumber: true })} />
                      </LabelWithError>
                    </div>
                    <div className="sm:col-span-6">
                      <LabelWithError title={strings.categories.form.bulletinSortOrder} error={undefined}>
                        <StyledInput type="number" {...register('sort_order_bulletin', { valueAsNumber: true })} />
                      </LabelWithError>
                    </div>
                  </div>
                </section>
                <div className="flex items-center justify-end space-x-4 px-1 pb-2 pt-4">
                  {!isEdit && (
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input type="checkbox" value="1" {...register('assign')} />
                      {strings.categories.form.assignToAllCustomers}
                    </label>
                  )}
                  {isEdit && (
                    <CommonButtonDefault type="button" danger onClick={() => setConfirmDelete(true)}>
                      {strings.button.delete}
                    </CommonButtonDefault>
                  )}
                  <CommonButtonDefault type="button" secondary onClick={() => navigate('/categories')}>
                    {strings.button.cancel}
                  </CommonButtonDefault>
                  <CommonButtonDefault type="submit" primary>
                    {strings.button.save}
                  </CommonButtonDefault>
                </div>
              </form>
            </div>
          </main>
          <CommonConfirmationModal
            open={confirmDelete}
            cancelled={() => setConfirmDelete(false)}
            confirmed={onDelete}
            danger
            title={strings.common.confirmation.deleteCategory.title}
            description={strings.common.confirmation.deleteCategory.description}
            buttonConfirm={strings.button.delete}
            buttonCancel={strings.button.cancel}
          />
        </>
      )}
    </div>
  );
}

CategoriesEditPageComponent.defaultProps = {
  isEdit: false,
};

export default CategoriesEditPageComponent;
